import { Dispatch, SetStateAction, FC, PropsWithChildren, ReactNode } from 'react';

import * as Collapsible from '@radix-ui/react-collapsible';

import {
  contentStyle,
  childrenContainerStyle,
  expandableRotatingChevronStyle,
  expandableStyle,
  indicatorButtonRecipe,
  triggerHeaderStyle,
} from './Expandable.css';
import { ChevronDownIcon } from '../../icons';
import { getClassNames } from '../../util/css';
import { ButtonOverlay } from '../ButtonOverlay/ButtonOverlay';
import { overlayButtonStyle } from '../ButtonOverlay/ButtonOverlay.css';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

type CompoundExpandableProps = {
  Content: typeof Content;
  Trigger: typeof Trigger;
};

type TriggerBasicProps = {
  className?: never;
  isTransparent?: boolean;
};

type TriggerLabelProps =
  | {
      label: string;
      'aria-labelledby'?: never;
    }
  | {
      'aria-labelledby': string;
      label?: never;
    };

type TriggerProps = TriggerBasicProps & TriggerLabelProps;

type StateProps =
  | {
      isOpen?: never;
      setOpen?: never;
      defaultOpen?: boolean;
    }
  | {
      /** Controlled state of the expandable */
      isOpen: boolean;
      /** State setter for the controlled state. Required in order to use built-in Expandable controls. */
      setOpen: Dispatch<SetStateAction<boolean>>;
      defaultOpen?: never;
    };
type ExpandableProps = {
  children: ReactNode;
} & StateProps;

export const Expandable: FC<ExpandableProps> & CompoundExpandableProps = ({
  children,
  setOpen,
  isOpen,
  defaultOpen,
}) => {
  return (
    <Collapsible.Root className={expandableStyle} defaultOpen={defaultOpen} onOpenChange={setOpen} open={isOpen}>
      {children}
    </Collapsible.Root>
  );
};

export const Content: FC<PropsWithChildren<unknown>> = ({ children }) => {
  return <Collapsible.Content className={contentStyle}>{children}</Collapsible.Content>;
};

export const Trigger: FC<PropsWithChildren<TriggerProps>> = ({
  label,
  'aria-labelledby': ariaLabelledby,
  children,
  className,
  isTransparent = false,
}) => {
  const indicatorButtonClassNames = getClassNames(indicatorButtonRecipe({ isTransparent }), overlayButtonStyle);

  return (
    <ButtonOverlay>
      <div className={getClassNames(triggerHeaderStyle, className)}>
        <Collapsible.Trigger asChild>
          <button aria-labelledby={ariaLabelledby} className={indicatorButtonClassNames}>
            {label ? <VisuallyHidden>{label}</VisuallyHidden> : null}
            <div className={expandableRotatingChevronStyle}>
              <ChevronDownIcon />
            </div>
          </button>
        </Collapsible.Trigger>
        <div className={childrenContainerStyle}>{children}</div>
      </div>
    </ButtonOverlay>
  );
};

Expandable.Content = Content;
Expandable.Trigger = Trigger;

Expandable.displayName = 'Expandable';
Expandable.Content.displayName = 'Expandable.Content';
Expandable.Trigger.displayName = 'Expandable.Trigger';
